import _ from "lodash";
import { useI18n } from "Hooks";
import { useCallback, useEffect, useMemo } from "react";
import { AI_WORKSHEET_TAGS } from "../modules/AiQuestionLoaderConstant";
import { cancelRequest } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/AiQuestionAssistantModule";

const {
  PLAN_START_TAG,
  PLAN_END_TAG,
  QUESTION_START_TAG,
  QUESTION_END_TAG,
  VISUALIZATION_START_TAG,
  VISUALIZATION_END_TAG,
} = AI_WORKSHEET_TAGS;

// * Safely triggers the active AI request cancellation.
const triggerCancelRequest = () => {
  const onCancelRequest = _.isFunction(cancelRequest) ? cancelRequest : _.noop;
  onCancelRequest();
};

/**
 * Extracts payload between start/end tags from the streamed content.
 * Returns an empty string until the end tag is received.
 */
const extractDataFromStreamedResponse = (content, start, end) => {
  if (!_.includes(content, end)) return "";
  let data = _.split(content, start);
  data = _.last(data);
  data = _.split(data, end);
  return _.first(data);
};

/**
 *
 * @param {*} content
 * @returns Classified streamed text response
 */
const getClassifiedResponse = content => {
  return {
    isStart: _.includes(content, PLAN_START_TAG),
    isPlanReceived: _.includes(content, PLAN_END_TAG),
    isQuestionReceived: _.includes(content, QUESTION_END_TAG),
    isVisualizationReceived: _.includes(content, VISUALIZATION_END_TAG),
    value: extractDataFromStreamedResponse(
      content,
      QUESTION_START_TAG,
      QUESTION_END_TAG
    ),
    visualizationValue: extractDataFromStreamedResponse(
      content,
      VISUALIZATION_START_TAG,
      VISUALIZATION_END_TAG
    ),
  };
};

/**
 *
 * @param {*} classifiedResponse
 * @param {*} t
 * @returns Loading Title
 */
const getLoadingTitle = (classifiedResponse, t) => {
  let loadingTitle = t("worksheet:creating_a_plan");

  if (_.isObject(classifiedResponse)) {
    const { isPlanReceived, isQuestionReceived } = classifiedResponse;

    if (isPlanReceived) {
      loadingTitle = t("worksheet:working_out_questions");
    } else if (isQuestionReceived) {
      loadingTitle = t("worksheet:generating_questions");
    }
  }

  return loadingTitle;
};

/**
 *
 * @param { messages, streamingState, goToSecondStep, resetAIHistory, onGenerateComplete }
 * @returns { loadingTitle, stopGeneration }
 * @description
 * - Parses streamed messages to classify response parts and determine loading title.
 * - Monitors streaming state to transition to the next step when plan and question are received.
 * - Provides a stopGeneration function to cancel the request and reset history.
 */
export const useAiQuestionPlanMaker = props => {
  const { t } = useI18n();
  const {
    messages,
    streamingState,
    goToSecondStep,
    resetAIHistory,
    onGenerateComplete,
  } = props;

  const classifiedResponse = useMemo(() => {
    const content = _.get(_.last(messages), "node.content");
    return getClassifiedResponse(content);
  }, [messages]);

  const loadingTitle = useMemo(() => {
    return getLoadingTitle(classifiedResponse, t);
  }, [classifiedResponse, t]);

  const enhancedResponse = useMemo(() => {
    return { ...classifiedResponse, loadingTitle };
  }, [classifiedResponse, loadingTitle]);

  const stopGeneration = useCallback(() => {
    triggerCancelRequest();
    resetAIHistory();
    onGenerateComplete();
  }, [resetAIHistory, onGenerateComplete]);

  useEffect(() => {
    // * Transition to the next step when both plan and question are received in the streamed response.
    if (
      streamingState === "END" &&
      enhancedResponse?.isQuestionReceived &&
      !_.isEmpty(_.get(enhancedResponse, "value"))
    ) {
      const value = _.get(enhancedResponse, "value");
      const visualization = _.get(enhancedResponse, "visualizationValue");
      triggerCancelRequest();
      goToSecondStep(value, visualization);
    }
  }, [enhancedResponse, goToSecondStep, streamingState]);

  return { ...enhancedResponse, stopGeneration };
};
